import React, { useRef, useState, useEffect } from "react";
import { Camera, VideoCamera, Sparkle, Check, Warning, Brain } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Disclaimer } from "@/components/Disclaimer";

const COMMON_EXERCISES = ["Squat", "Bench Press", "Deadlift", "Overhead Press", "Pull-Up", "Push-Up", "Row", "Lunge"];

export default function FormCheck() {
  const fileRef = useRef(null);
  const [exercise, setExercise] = useState("Squat");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/ai/form-checks").then(({ data }) => setHistory(data));
  }, []);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("Max 25MB");
      return;
    }
    setFile(f);
    setResult(null);
    if (f.type.startsWith("image/")) {
      setPreview({ kind: "image", url: URL.createObjectURL(f) });
    } else if (f.type.startsWith("video/")) {
      setPreview({ kind: "video", url: URL.createObjectURL(f) });
    } else {
      toast.error("Use an image or video file");
      setFile(null);
    }
  };

  const analyze = async () => {
    if (!file || !exercise) return;
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/ai/form-check?exercise=${encodeURIComponent(exercise)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setResult(data);
      setHistory([data, ...history]);
      toast.success("Form analyzed.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Analysis failed";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const r = result?.result;
  const verdictColor = r?.score >= 80 ? "text-green-400" : r?.score >= 60 ? "text-brand-accent" : "text-orange-400";

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="formcheck-page">
      <div className="mb-8">
        <div className="label-tiny text-brand-accent mb-2 flex items-center gap-2">
          <Sparkle size={12} weight="fill" /> PRO FEATURE · CLAUDE VISION
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl">AI FORM CHECK</h1>
        <p className="text-text-secondary text-sm mt-2 max-w-xl">
          Upload a photo or short video. Claude Sonnet 4.5 analyzes your form and returns specific cues.
        </p>
      </div>

      <Disclaimer variant="ai" className="mb-6 max-w-3xl" testid="formcheck-ai-disclaimer" />

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="tactical-card p-6 mb-4">
            <h3 className="font-heading text-lg mb-4">UPLOAD MEDIA</h3>
            <div className="mb-4">
              <label className="label-tiny block mb-2">EXERCISE</label>
              <Input value={exercise} onChange={(e) => setExercise(e.target.value)} className="bg-surface-elevated border-white/10" data-testid="formcheck-exercise-input" />
              <div className="flex flex-wrap gap-1 mt-2">
                {COMMON_EXERCISES.map((ex) => (
                  <button
                    key={ex}
                    onClick={() => setExercise(ex)}
                    className={`text-xs px-2 py-1 border transition-colors ${exercise === ex ? "border-brand-accent text-brand-accent" : "border-white/10 text-text-secondary hover:border-white/40"}`}
                    data-testid={`exercise-chip-${ex}`}
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-white/20 hover:border-brand-accent transition-colors p-8 text-center"
              data-testid="formcheck-upload-zone"
            >
              {preview?.kind === "image" && <img src={preview.url} alt="" className="max-h-48 mx-auto mb-3" />}
              {preview?.kind === "video" && (
                <video src={preview.url} controls className="max-h-48 mx-auto mb-3" />
              )}
              {!preview && (
                <>
                  <div className="flex justify-center gap-2 mb-3">
                    <Camera size={28} weight="duotone" className="text-brand-accent" />
                    <VideoCamera size={28} weight="duotone" className="text-brand-primary" />
                  </div>
                  <div className="label-tiny">CLICK TO UPLOAD IMAGE OR VIDEO</div>
                  <div className="text-xs text-text-secondary mt-1">Max 25MB · JPG, PNG, MP4, MOV</div>
                </>
              )}
              {preview && <div className="label-tiny mt-2 text-brand-accent">CLICK TO CHANGE</div>}
            </button>
            <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={onPick} data-testid="formcheck-file-input" />

            <button
              onClick={analyze}
              disabled={!file || loading || !exercise}
              className="btn-kinetic w-full mt-4 py-3 text-xs label-tiny tracking-widest disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="formcheck-analyze-button"
            >
              {loading ? "ANALYZING..." : <><Brain size={14} weight="bold" /> ANALYZE FORM</>}
            </button>
          </div>

          {history.length > 0 && (
            <div className="tactical-card p-5">
              <div className="label-tiny mb-3">// HISTORY</div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {history.map((h) => (
                  <button
                    key={h.check_id}
                    onClick={() => setResult(h)}
                    className={`w-full text-left border p-3 transition-colors ${result?.check_id === h.check_id ? "border-brand-accent" : "border-white/10 hover:border-white/40"}`}
                    data-testid={`history-${h.check_id}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-heading text-sm">{h.exercise}</span>
                      <span className="font-heading text-lg tabular-nums text-brand-accent">{h.result?.score || 0}</span>
                    </div>
                    <div className="label-tiny text-text-secondary mt-1">
                      {h.media_type === "video" ? `${h.frame_count} frames` : "Photo"} · {new Date(h.created_at).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          {loading && (
            <div className="tactical-card p-12 text-center" data-testid="formcheck-loading">
              <Brain size={48} weight="duotone" className="text-brand-accent mx-auto mb-4 animate-pulse" />
              <h3 className="font-heading text-xl">CLAUDE IS ANALYZING</h3>
              <p className="text-sm text-text-secondary mt-2">This usually takes 15-30 seconds...</p>
            </div>
          )}
          {!loading && r && (
            <div className="tactical-card p-6 animate-fade-up" data-testid="formcheck-result">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
                <div>
                  <div className="label-tiny mb-1">FORM SCORE</div>
                  <div className="flex items-baseline gap-2">
                    <span className={`font-heading text-6xl tabular-nums ${verdictColor}`}>{r.score}</span>
                    <span className="text-text-secondary">/ 100</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="label-tiny mb-1">VERDICT</div>
                  <div className={`font-heading text-xl ${verdictColor}`}>{r.verdict?.toUpperCase()}</div>
                </div>
              </div>

              {r.strengths?.length > 0 && (
                <div className="mb-5">
                  <div className="label-tiny mb-2 flex items-center gap-1"><Check size={12} weight="bold" className="text-green-400" /> STRENGTHS</div>
                  <ul className="space-y-1.5">
                    {r.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-text-secondary flex gap-2">
                        <span className="text-green-400">▸</span> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {r.issues?.length > 0 && (
                <div className="mb-5">
                  <div className="label-tiny mb-2 flex items-center gap-1"><Warning size={12} weight="bold" className="text-orange-400" /> FIX THESE</div>
                  <div className="space-y-3">
                    {r.issues.map((iss, i) => (
                      <div key={i} className="border-l-2 border-orange-400 pl-3">
                        <div className="text-sm font-heading">{iss.area?.toUpperCase()}</div>
                        <div className="text-xs text-text-secondary mt-1">{iss.problem}</div>
                        <div className="text-xs text-brand-accent mt-1">→ {iss.fix}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {r.cues?.length > 0 && (
                <div className="pt-4 border-t border-white/10">
                  <div className="label-tiny mb-3 text-brand-accent">COACH'S CUES FOR NEXT SET</div>
                  <div className="space-y-2">
                    {r.cues.map((c, i) => (
                      <div key={i} className="bg-surface-elevated p-3 text-sm">
                        <span className="text-brand-accent font-heading mr-2">{i + 1}.</span>{c}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {!loading && !r && (
            <div className="tactical-card p-12 text-center" data-testid="formcheck-empty">
              <Sparkle size={36} weight="duotone" className="text-brand-accent mx-auto mb-4" />
              <h3 className="font-heading text-xl mb-2">READY WHEN YOU ARE</h3>
              <p className="text-sm text-text-secondary">Upload media and hit "ANALYZE FORM" to get AI coaching feedback.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
